import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { intelligenceLabService, type LabUsageAdmin, type LabUsageAgent, type PricingCatalogRow } from '../services/api'
import { AlertCircle, RefreshCw, Search, ChevronLeft, ChevronRight, DollarSign, Zap, MessageSquare } from 'lucide-react'

function fmt(n: number, decimals = 0) {
  return n.toLocaleString('fr-FR', { maximumFractionDigits: decimals })
}

function fmtCost(n: number) {
  return `$${n.toFixed(4)}`
}

function cacheTokens(totals: {
  cachedInputTokens?: number
  cacheReadTokens?: number
  cacheWriteTokens?: number
}) {
  const cached = totals.cachedInputTokens ?? totals.cacheReadTokens ?? 0
  return cached + (totals.cacheWriteTokens ?? 0)
}

function prevMonth(m: string) {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(y, mo - 2, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function nextMonth(m: string) {
  const [y, mo] = m.split('-').map(Number)
  const d = new Date(y, mo, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function currentMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function StatCard({ icon: Icon, label, value, sub }: { icon: React.ElementType; label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white border rounded-xl p-4 flex items-start gap-3">
      <div className="p-2 rounded-lg bg-blue-50 text-primary">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-secondary">{label}</p>
        <p className="text-xl font-bold text-primary mt-0.5">{value}</p>
        {sub && <p className="text-xs text-tertiary mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

function AgentsTable({ agents, search }: { agents: LabUsageAgent[]; search: string }) {
  const filtered = useMemo(
    () =>
      !search
        ? agents
        : agents.filter(a => a.agentId.toLowerCase().includes(search.toLowerCase())),
    [agents, search],
  )

  const total = agents.reduce((s, a) => s + a.estimatedCostUsd, 0)

  return (
    <div className="overflow-x-auto rounded-xl border bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="text-left px-4 py-3 text-secondary font-medium">Agent</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Appels</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Tokens entrée</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Tokens cache</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Tokens sortie</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Total tokens</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Coût estimé</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center py-8 text-tertiary">
                Aucun agent ne correspond
              </td>
            </tr>
          ) : (
            filtered.map(agent => (
              <tr key={agent.agentId} className="border-b hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-mono text-primary text-xs">{agent.agentId}</span>
                  {agent.lastUsedAt && (
                    <span className="block text-xs text-tertiary">
                      Dernier : {new Date(agent.lastUsedAt).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-primary">{fmt(agent.calls)}</td>
                <td className="px-4 py-3 text-right text-primary">{fmt(agent.inputTokens)}</td>
                <td className="px-4 py-3 text-right text-primary">{fmt(cacheTokens(agent))}</td>
                <td className="px-4 py-3 text-right text-primary">{fmt(agent.outputTokens)}</td>
                <td className="px-4 py-3 text-right text-primary">{fmt(agent.totalTokens)}</td>
                <td className="px-4 py-3 text-right font-medium text-success">{fmtCost(agent.estimatedCostUsd)}</td>
              </tr>
            ))
          )}
        </tbody>
        {filtered.length > 0 && (
          <tfoot>
            <tr className="border-t bg-gray-50">
              <td className="px-4 py-2 text-secondary font-medium text-xs">Total affiché</td>
              <td colSpan={5} />
              <td className="px-4 py-2 text-right font-bold text-success">
                {fmtCost(filtered.reduce((s, a) => s + a.estimatedCostUsd, 0))}
                {filtered.length < agents.length && (
                  <span className="ml-1 text-xs text-tertiary">/ {fmtCost(total)}</span>
                )}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

function counted(value: number | undefined, fallback: number) {
  return typeof value === 'number' ? value : fallback
}

function DailyTable({ data }: { data: LabUsageAdmin['daily'] }) {
  const rows = useMemo(
    () =>
      [...data]
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(day => ({
          date: day.date,
          calls: counted(day.calls, day.agents.reduce((s, a) => s + a.calls, 0)),
          totalTokens: counted(day.totalTokens, day.agents.reduce((s, a) => s + a.totalTokens, 0)),
          cost: counted(day.estimatedCostUsd, day.agents.reduce((s, a) => s + a.estimatedCostUsd, 0)),
          agentCount: day.agents.length,
        })),
    [data],
  )

  return (
    <div className="overflow-x-auto rounded-xl border bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="text-left px-4 py-3 text-secondary font-medium">Date</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Agents actifs</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Appels</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Total tokens</th>
            <th className="text-right px-4 py-3 text-secondary font-medium">Coût estimé</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-8 text-tertiary">
                Aucune donnée pour ce mois
              </td>
            </tr>
          ) : (
            rows.map(row => (
              <tr key={row.date} className="border-b hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-primary">
                  {new Date(row.date + 'T00:00:00').toLocaleDateString('fr-FR', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}
                </td>
                <td className="px-4 py-3 text-right text-secondary">{row.agentCount}</td>
                <td className="px-4 py-3 text-right text-primary">{fmt(row.calls)}</td>
                <td className="px-4 py-3 text-right text-primary">{fmt(row.totalTokens)}</td>
                <td className="px-4 py-3 text-right font-medium text-success">{fmtCost(row.cost)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

const PRICE_PROVIDERS = ['deepseek', 'anthropic', 'openai'] as const

function catalogError(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: unknown } } }).response?.data
    const payload = data?.error
    const message = typeof payload === 'string'
      ? payload
      : typeof payload === 'object' && payload !== null && 'message' in payload
        ? (payload as { message: unknown }).message
        : ''
    if (typeof message === 'string' && message.trim() !== '') return message
  }
  return 'Enregistrement du catalogue impossible.'
}

function PricingCatalogEditor() {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery(
    ['intelligence-pricing-catalog'],
    () => intelligenceLabService.getPricingCatalog(),
  )
  const [rows, setRows] = useState<PricingCatalogRow[] | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const draft = rows ?? data?.rows ?? []

  function updateRow(index: number, patch: Partial<PricingCatalogRow>) {
    setRows(draft.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)))
  }

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      const saved = await intelligenceLabService.savePricingCatalog(draft)
      setRows(saved.rows)
      await queryClient.invalidateQueries(['intelligence-lab-usage'])
    } catch (saveFailure) {
      setSaveError(catalogError(saveFailure))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-bold text-primary">Barème</h2>
        <p className="text-sm text-secondary mt-1">
          Dollars pour un million de tokens. Intelligence envoie les quantités, ATF recalcule le montant
          avec la ligne en vigueur ce jour-là. Une nouvelle date s’applique à partir de ce jour.
          L’écriture de cache est comptée au tarif d’entrée.
        </p>
      </div>
      {error instanceof Error ? (
        <p className="text-sm text-error">{error.message}</p>
      ) : error ? (
        <p className="text-sm text-error">Catalogue indisponible.</p>
      ) : null}
      {isLoading && <p className="text-sm text-secondary">Chargement du barème…</p>}
      {data && (
        <>
          <div className="overflow-x-auto rounded-xl border bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-3 py-2 text-secondary font-medium">Fournisseur</th>
                  <th className="text-left px-3 py-2 text-secondary font-medium">Modèle</th>
                  <th className="text-left px-3 py-2 text-secondary font-medium">Date</th>
                  <th className="text-right px-3 py-2 text-secondary font-medium">Entrée</th>
                  <th className="text-right px-3 py-2 text-secondary font-medium">Cache</th>
                  <th className="text-right px-3 py-2 text-secondary font-medium">Sortie</th>
                  <th className="text-left px-3 py-2 text-secondary font-medium">Alias</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draft.map((row, index) => (
                  <tr key={`${row.provider}-${row.model}-${row.effectiveFrom}-${index}`} className="border-b">
                    <td className="px-3 py-2">
                      <select id={`price-provider-${index}`} name="price-provider"
                        value={row.provider}
                        onChange={event => updateRow(index, { provider: event.target.value as PricingCatalogRow['provider'] })}
                        className="form-input"
                      >
                        {PRICE_PROVIDERS.map(provider => (
                          <option key={provider} value={provider}>{provider}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input id={`price-model-${index}`} name="price-model"
                        value={row.model}
                        onChange={event => updateRow(index, { model: event.target.value })}
                        className="form-input w-full"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input id={`price-from-${index}`} name="price-from"
                        type="date"
                        value={row.effectiveFrom}
                        onChange={event => updateRow(index, { effectiveFrom: event.target.value })}
                        className="form-input"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input id={`price-input-${index}`} name="price-input"
                        type="number"
                        min="0"
                        step="0.0001"
                        value={row.inputPerMTokens}
                        onChange={event => updateRow(index, { inputPerMTokens: Number(event.target.value) })}
                        className="form-input text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input id={`price-cache-${index}`} name="price-cache"
                        type="number"
                        min="0"
                        step="0.0001"
                        value={row.cachePerMTokens}
                        onChange={event => updateRow(index, { cachePerMTokens: Number(event.target.value) })}
                        className="form-input text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input id={`price-output-${index}`} name="price-output"
                        type="number"
                        min="0"
                        step="0.0001"
                        value={row.outputPerMTokens}
                        onChange={event => updateRow(index, { outputPerMTokens: Number(event.target.value) })}
                        className="form-input text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input id={`price-aliases-${index}`} name="price-aliases"
                        value={row.aliases.join(', ')}
                        onChange={event => updateRow(index, {
                          aliases: event.target.value.split(',').map(alias => alias.trim()).filter(alias => alias !== ''),
                        })}
                        className="form-input w-full"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setRows(draft.filter((_, rowIndex) => rowIndex !== index))}
                        className="button button--secondary"
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {saveError && <p className="text-sm text-error">{saveError}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRows([
                ...draft,
                {
                  provider: 'deepseek',
                  model: '',
                  aliases: [],
                  effectiveFrom: new Date().toISOString().slice(0, 10),
                  inputPerMTokens: 0,
                  cachePerMTokens: 0,
                  outputPerMTokens: 0,
                },
              ])}
              className="button button--secondary"
            >
              Ajouter une date
            </button>
            <button type="button" onClick={save} disabled={saving} className="button button--primary">
              {saving ? 'Enregistrement…' : 'Enregistrer le barème'}
            </button>
          </div>
        </>
      )}
    </section>
  )
}

export function SpendingTracker() {
  const [month, setMonth] = useState(currentMonth)
  const [agentSearch, setAgentSearch] = useState('')
  const [tab, setTab] = useState<'agents' | 'daily'>('agents')

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<LabUsageAdmin, Error>(
    ['intelligence-lab-usage', month],
    () => intelligenceLabService.getUsageAdmin(month),
    { retry: 1, staleTime: 60_000 },
  )

  const monthLabel = useMemo(() => {
    const [y, m] = month.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
  }, [month])

  return (
    <div className="page-inset p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">Suivi des dépenses</h1>
          <p className="text-sm text-secondary mt-1">Consommation LLM via Intelligence</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonth(prevMonth(month))}
            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-primary transition-colors"
            title="Mois précédent"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-2 text-sm font-medium text-primary bg-gray-100 rounded-lg min-w-32 text-center capitalize">
            {monthLabel}
          </span>
          <button
            onClick={() => setMonth(nextMonth(month))}
            disabled={month >= currentMonth()}
            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Mois suivant"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-primary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-error-light border text-error">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Impossible de charger les données</p>
            <p className="text-sm mt-0.5">{error.message}</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20 text-secondary">
          <RefreshCw className="w-6 h-6 animate-spin mr-3" />
          Chargement des dépenses…
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={DollarSign}
              label="Coût estimé total"
              value={fmtCost(data.totals.estimatedCostUsd)}
              sub={
                data.totals.isEstimateComplete === false
                  ? `${data.currency} · tarif incomplet`
                  : data.currency
              }
            />
            <StatCard
              icon={Zap}
              label="Total tokens"
              value={fmt(data.totals.totalTokens)}
              sub={`${fmt(data.totals.inputTokens)} in · ${fmt(cacheTokens(data.totals))} cache · ${fmt(data.totals.outputTokens)} out`}
            />
            <StatCard
              icon={MessageSquare}
              label="Appels LLM"
              value={fmt(data.totals.calls)}
              sub={`${data.agents.length} agent${data.agents.length !== 1 ? 's' : ''} actif${data.agents.length !== 1 ? 's' : ''}`}
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b">
            {(['agents', 'daily'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  tab === t
                    ? 'border-blue-600 text-primary'
                    : 'border-transparent text-secondary'
                }`}
              >
                {t === 'agents' ? 'Par agent' : 'Par jour'}
              </button>
            ))}
          </div>

          {tab === 'agents' && (
            <>
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
                <input id="spending-agent-filter" name="spending-agent-filter"
                  type="text"
                  placeholder="Filtrer par agent…"
                  value={agentSearch}
                  onChange={e => setAgentSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-sm text-primary focus:outline-none"
                />
              </div>
              <AgentsTable agents={data.agents} search={agentSearch} />
            </>
          )}

          {tab === 'daily' && <DailyTable data={data.daily} />}
        </>
      )}

      <PricingCatalogEditor />
    </div>
  )
}
